import type React from 'react';

import LoadingCard from '@/components/ui/loading-card';
import { InstitutionFuturesProgressStep } from '@/context/progress';
import { RouteConfigProvider } from '@/context/route-config';
import FuturesClientCategorisationTreatment from '@/features/futures/components/client-categorisation-treatment';
import FuturesClientCategorisationLayout from '@/features/futures/layouts/client-categorisation';
import { useProfileInstitutionDetailsAnswers, useProfileInstitutionDetailsMeta } from '@/hooks/queries/use-profile-institution-details';

const InstitutionOnboardingFuturesClientCategorisationTreatmentLayout = (props: React.ComponentPropsWithoutRef<typeof FuturesClientCategorisationLayout>) => {
  return <FuturesClientCategorisationLayout progress={0.5} step={InstitutionFuturesProgressStep.CLIENT_CATEGORISATION} {...props} />;
};
InstitutionOnboardingFuturesClientCategorisationTreatmentLayout.displayName = 'InstitutionOnboardingFuturesClientCategorisationTreatmentLayout';

const InstitutionOnboardingFuturesClientCategorisationTreatmentLoading = () => {
  return (
    <InstitutionOnboardingFuturesClientCategorisationTreatmentLayout>
      <LoadingCard />
    </InstitutionOnboardingFuturesClientCategorisationTreatmentLayout>
  );
};
InstitutionOnboardingFuturesClientCategorisationTreatmentLoading.displayName = 'InstitutionOnboardingFuturesClientCategorisationTreatmentLoading';

const InstitutionOnboardingFuturesClientCategorisationTreatmentContent = () => {
  const { isLoading: isLoadingInstitutionDetailsAnswers, data: institutionDetailsAnswers } = useProfileInstitutionDetailsAnswers();
  const {
    isFetching: isFetchingInstitutionDetailsMeta,
    isLoading: isLoadingInstitutionDetailsMeta,
    data: { clientCategorisation, isRegulated },
  } = useProfileInstitutionDetailsMeta();
  const isLoading = isLoadingInstitutionDetailsAnswers || isFetchingInstitutionDetailsMeta || isLoadingInstitutionDetailsMeta;
  const countryOfIncorporation = institutionDetailsAnswers?.incorporation?.countryOfIncorporation;

  if (isLoading) return <InstitutionOnboardingFuturesClientCategorisationTreatmentLoading />;

  if (!countryOfIncorporation) throw new Error('Unable to proceed with client categorisation, no country of incorporation');

  if (!clientCategorisation) throw new Error('Unable to proceed with client categorisation, no client categorisation');

  return (
    <InstitutionOnboardingFuturesClientCategorisationTreatmentLayout>
      <FuturesClientCategorisationTreatment
        clientCategorisation={clientCategorisation}
        countryOfResidency={countryOfIncorporation}
        reprocessFuturesEligibility={isRegulated ?? undefined}
      />
    </InstitutionOnboardingFuturesClientCategorisationTreatmentLayout>
  );
};
InstitutionOnboardingFuturesClientCategorisationTreatmentContent.displayName = 'InstitutionOnboardingFuturesClientCategorisationTreatmentContent';

function InstitutionOnboardingFuturesClientCategorisationTreatmentPage() {
  return (
    <RouteConfigProvider
      config={{
        appropriatenessTest: '/institution/onboarding/futures/appropriateness-test',
        jurisdictionUnavailable: '/institution/onboarding/futures/client-categorisation/jurisdiction-unavailable',
        professionalAgreement: '/institution/onboarding/futures/client-categorisation/professional-agreement',
      }}
    >
      <InstitutionOnboardingFuturesClientCategorisationTreatmentContent />
    </RouteConfigProvider>
  );
}
InstitutionOnboardingFuturesClientCategorisationTreatmentPage.displayName = 'InstitutionOnboardingFuturesClientCategorisationTreatmentPage';

export default InstitutionOnboardingFuturesClientCategorisationTreatmentPage;
